// ═══════════════════════════════════════════════════════════════════════════
// AI → EditorCommand translator — LAYER-MANAGEMENT domain (T7, agent-bridge)
// ═══════════════════════════════════════════════════════════════════════════
//
// Translates the LAYER-management AI shapes (CREATE_LAYER / RENAME_LAYER /
// SET_ACTIVE_LAYER) into the internal `EditorCommand`s the store dispatches.
// Every path runs the CC-2a fail-closed guard (`assertAiCallableLayerAction`)
// BEFORE producing any EditorCommand, so the safe contour rejects a
// non-AI-callable / unknown target without mutating the document. Geometry
// is only ever changed by the reducer the returned commands flow into —
// never here — honouring the CLAUDE.md rules:
//   "AI drafting … может только предлагать и исполнять команды через
//    безопасный контур." + "любые изменения геометрии только через Command
//    Engine."
//
// The thin per-action translators live in the PACKAGE-INTERNAL sibling
// `layerTranslators.ts` (NOT re-exported by the barrel), so the ONLY public
// LAYER entry is the guarded `translateLayerCommand` below. Mirrors every
// other domain (BLOCK / ENTITY / DRAW-MODIFY / VIEW-SPACE / MATCHPROP /
// BASE-PRIM), preventing a consumer of `@dxf-ai-editor/ai-commands` from
// emitting a geometry/table command past the fail-closed guard.

import type { EditorCommand } from "@dxf-ai-editor/contracts";
import {
  ALL_COMMAND_DESCRIPTORS,
  type CommandDescriptor,
} from "@dxf-ai-editor/command-registry";

import { type AiDispatchContext } from "./blockDispatcherShared";
import { assertAiCallableLayerAction } from "./layerGuard";
import type { LayerAiCommand } from "./layerSchemas";
import {
  translateCreateLayer,
  translateRenameLayer,
  translateSetActiveLayer,
} from "./layerTranslators";

// Re-export the LAYER safe-contour guard (mirrors blockDispatcher /
// basePrimDispatcher re-exporting their guards) so consumers can reach it
// from this domain entry point.
export {
  LAYER_AI_ACTION_TARGET_COMMAND,
  assertAiCallableLayerAction,
} from "./layerGuard";

/**
 * Top-level LAYER-management dispatcher. Runs the CC-2a fail-closed guard,
 * then routes the AI command to the translator that owns its `action`. Every
 * LAYER action is self-contained (no live document / active-space context
 * needed) — the `context` parameter is accepted only for signature parity
 * with the sibling domain dispatchers `aiDispatcher.ts` routes through.
 * Returns the flat `EditorCommand[]` the store should dispatch (the caller
 * wraps the batch in one grouped-undo unit — see the app safe contour).
 */
export function translateLayerCommand(
  command: LayerAiCommand,
  _context: AiDispatchContext = {},
  descriptors: readonly CommandDescriptor[] = ALL_COMMAND_DESCRIPTORS,
): EditorCommand[] {
  // Safe-contour gate (CC-2a): refuse to translate any action whose target
  // command is not `aiCallable: true`. Fails closed before any EditorCommand
  // is produced. `descriptors` is injectable for tests.
  assertAiCallableLayerAction(command.action, descriptors);
  switch (command.action) {
    case "CREATE_LAYER":
      return translateCreateLayer(command);
    case "RENAME_LAYER":
      return translateRenameLayer(command);
    case "SET_ACTIVE_LAYER":
      return translateSetActiveLayer(command);
  }
}

/**
 * Batch variant — flatten-translates a list of LAYER-management AI commands
 * in order via {@link translateLayerCommand} (each guarded individually).
 */
export function translateLayerCommandBatch(
  commands: readonly LayerAiCommand[],
  context: AiDispatchContext = {},
  descriptors: readonly CommandDescriptor[] = ALL_COMMAND_DESCRIPTORS,
): EditorCommand[] {
  const all: EditorCommand[] = [];
  for (const command of commands) {
    all.push(...translateLayerCommand(command, context, descriptors));
  }
  return all;
}
